import { useQuery, useQueryClient } from '@tanstack/react-query';
import { failuresApi } from '@/services/api/failures';
import { vblApi } from '@/services/api/vblApi';
import { filterOptionsService } from '@/services/api/filterOptions';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types';
import { logApiError } from '@/services/api/utils';

/**
 * React Query hooks for failures data management with multi-select filters
 * Provides caching, loading states, and error handling for failure data
 */

// The backend ignores operationTimeMin/Max and dateFrom/To — they're applied
// client-side after fetch — so they must not change the cache key, or every
// keystroke in the Advanced Filters refetches the same dataset.
const stripClientOnlyRangeFields = (filters: FailureFilters): Omit<FailureFilters, 'operationTimeMin' | 'operationTimeMax' | 'dateFrom' | 'dateTo'> => {
  const { operationTimeMin: _min, operationTimeMax: _max, dateFrom: _from, dateTo: _to, ...rest } = filters;
  return rest;
};

// Query keys for cache management
export const failuresQueryKeys = {
  all: ['failures'] as const,
  lists: () => [...failuresQueryKeys.all, 'list'] as const,
  list: (filters: FailureFilters) => [...failuresQueryKeys.lists(), stripClientOnlyRangeFields(filters)] as const,
  listLegacy: (filters: Record<string, unknown>) =>
    [...failuresQueryKeys.lists(), 'legacy', filters] as const,
  details: () => [...failuresQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...failuresQueryKeys.details(), id] as const,
  filterOptions: () => [...failuresQueryKeys.all, 'filterOptions'] as const,
  weibullData: (filters: FailureFilters) => [...failuresQueryKeys.all, 'weibull', stripClientOnlyRangeFields(filters)] as const,
  statistics: (filters: FailureFilters) =>
    [...failuresQueryKeys.all, 'statistics', stripClientOnlyRangeFields(filters)] as const,
} as const;

// VBL Query keys for cache management
export const vblQueryKeys = {
  all: ['vbl'] as const,
  lists: () => [...vblQueryKeys.all, 'list'] as const,
  list: (filters: FailureFilters) => [...vblQueryKeys.lists(), stripClientOnlyRangeFields(filters)] as const,
  analysis: (filters: FailureFilters) => [...vblQueryKeys.all, 'analysis', stripClientOnlyRangeFields(filters)] as const,
  chartData: (filters: FailureFilters, turbineId?: string) => [...vblQueryKeys.all, 'chartData', stripClientOnlyRangeFields(filters), turbineId] as const,
  vibrationData: (turbineId: string) => [...vblQueryKeys.all, 'vibrationData', turbineId] as const,
  filterOptions: () => [...vblQueryKeys.all, 'filterOptions'] as const,
  statistics: (filters: FailureFilters) => [...vblQueryKeys.all, 'statistics', stripClientOnlyRangeFields(filters)] as const,
} as const;

/**
 * Hook to fetch failures data with multi-select filtering and pagination
 * Enhanced with proper error handling and logging
 */
export function useFailures(filters: FailureFilters = DEFAULT_FAILURE_FILTERS) {
  return useQuery({
    queryKey: failuresQueryKeys.list(filters),
    queryFn: async () => {
      try {
        return await failuresApi.getFailures(filters);
      } catch (error) {
        logApiError(error, 'useFailures');
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000, // Use cacheTime for v4
  });
}

/**
 * Legacy hook for backward compatibility with single-select filters
 */
export function useFailuresLegacy(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: failuresQueryKeys.listLegacy(filters),
    queryFn: () => failuresApi.getFailuresLegacy(filters),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch a specific failure record
 */
export function useFailure(id: string) {
  return useQuery({
    queryKey: failuresQueryKeys.detail(id),
    queryFn: () => failuresApi.getFailureById(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch filter options with optimized caching
 */
export function useFilterOptions() {
  return useQuery({
    queryKey: failuresQueryKeys.filterOptions(),
    queryFn: () => filterOptionsService.getFilterOptions(),
    staleTime: 15 * 60 * 1000, // 15 minutes - filter options change rarely
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch filter options for ONYX data set with optimised caching
 */
export function useOnyxFilterOptions() {
  return useQuery({
    queryKey: failuresQueryKeys.filterOptions(),
    queryFn: () => filterOptionsService.getOnyxFilters(),
    staleTime: 15 * 60 * 1000, // 15 minutes - filter options change rarely
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
}


/**
 * Hook to fetch failures data optimized for Weibull analysis
 * Includes operation times and summary statistics
 */
export function useFailuresForWeibull(filters: FailureFilters = DEFAULT_FAILURE_FILTERS) {
  return useQuery({
    queryKey: failuresQueryKeys.weibullData(filters),
    queryFn: () => failuresApi.getFailuresForWeibullAnalysis(filters, false, null),
    staleTime: 5 * 60 * 1000,
    select: (data) => ({
      ...data,
      // Pre-process data for Weibull analysis
      validOperationTimes: data.operationTimes.filter((time) => time > 0),
      sortedOperationTimes: [...data.operationTimes].sort((a, b) => a - b),
    }),
  });
}

/**
 * Hook to get detailed filter statistics
 */
export function useFailureStatistics(filters: FailureFilters = DEFAULT_FAILURE_FILTERS) {
  return useQuery({
    queryKey: failuresQueryKeys.statistics(filters),
    queryFn: () => failuresApi.getFilterStatistics(filters),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to invalidate all failures cache (useful after data updates)
 */
export function useInvalidateFailures() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: failuresQueryKeys.all });
  };
}

/**
 * Hook for real-time failure metrics computed from current data
 */
export function useFailureMetrics(filters: FailureFilters = DEFAULT_FAILURE_FILTERS) {
  const { data, isLoading } = useFailures({ ...filters, size: 1000 });

  const metrics = data?.failures
    ? {
      totalCount: data.total_count,
      avgOperationTime:
        data.failures.reduce((sum, f) => sum + f.operation_time_years, 0) / data.failures.length,
      siteCount: new Set(data.failures.map((f) => f.site_name)).size,
      componentDistribution: data.failures.reduce(
        (acc, f) => {
          acc[f.component_major] = (acc[f.component_major] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
      failureTypeDistribution: data.failures.reduce(
        (acc, f) => {
          acc[f.outcome_name] = (acc[f.outcome_name] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
      operationTimeRange: {
        min: Math.min(...data.failures.map((f) => f.operation_time_years)),
        max: Math.max(...data.failures.map((f) => f.operation_time_years)),
      },
    }
    : null;

  return {
    metrics,
    isLoading,
    hasData: !!data?.failures?.length,
  };
}

/**
 * Hook to fetch VBL data with filtering (simple pattern like useFailures)
 */
export function useVblData(filters: FailureFilters = DEFAULT_FAILURE_FILTERS) {
  return useQuery({
    queryKey: vblQueryKeys.list(filters),
    queryFn: async () => {
      try {
        return await vblApi.getVblData(filters);
      } catch (error) {
        logApiError(error, 'useVblData');
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
}
